import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import {
  Box, Card, CardContent, Tab, TextField, CircularProgress,
} from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import useFetchApi from '../../../hooks/useFetchApi';
import { GetSectorRootsConfigResponse } from '../../../requests/api/apiTypes';
import apiClient from '../../../requests/api/apiClient';
import { FetchError } from '../../../views/types';
import { ROOTS_NAME } from '../../../constants/graphs';
import ConversationsTab from '../ConversationTab';
import useStyles from '../styles';
import { DEFAULT_IRRIGATION_THRESHOLD, DEFAULT_ROOTS_THRESHOLD } from './builder';
import { ModalState, EMPTY_MODAL_STATE } from '../chartModal';
import useIsClientOperator from '../../../hooks/useIsClientOperator';

const RootsConfigContent = ({
  sectorId,
  setModalState: setModalStateProp,
  refreshCounter = 0,
  onConfigSaved,
}: {
  sectorId: string;
  setModalState?: React.Dispatch<React.SetStateAction<ModalState>>;
  refreshCounter?: number;
  onConfigSaved?: () => void;
}) => {
  const classes = useStyles();
  const { search } = useLocation();
  const queryParams = new URLSearchParams(search);
  const { enqueueSnackbar } = useSnackbar();

  // Default to the conversations tab when arriving from a notification (date param).
  const date = queryParams.get('date');
  const initialTabValue = date ? '3' : '1';

  const [tabValue, setTabValue] = useState(initialTabValue);
  const [isLoading, setIsLoading] = useState(true);
  const [irrigationThreshold, setIrrigationThreshold] = useState<number>(0);
  const [rootsThreshold, setRootsThreshold] = useState<number>(0);

  const [, setInternalModalState] = useState<ModalState>(EMPTY_MODAL_STATE);
  const setModalState = setModalStateProp ?? setInternalModalState;

  const isClientOperator = useIsClientOperator();

  useEffect(() => {
    if (isClientOperator && tabValue === '1') {
      setTabValue('2');
    }
  }, [isClientOperator, tabValue]);

  const { data, loading } = useFetchApi<GetSectorRootsConfigResponse>({
    modelEndpoint: 'rootsConfigDetail',
    modelName: 'rootsConfig',
    id: sectorId,
    dependencies: [sectorId],
  });

  useEffect(() => {
    if (!loading) {
      setIrrigationThreshold(
        data?.rootsConfig?.irrigationThreshold || DEFAULT_IRRIGATION_THRESHOLD,
      );
      setRootsThreshold(data?.rootsConfig?.rootsThreshold || DEFAULT_ROOTS_THRESHOLD);
      setIsLoading(false);
    }
  }, [data, loading]);

  const handleTabChange = useCallback((event: React.SyntheticEvent, newValue: string) => {
    setTabValue(newValue);
  }, []);

  const handleThresholdsChange = useCallback(async (
    value: number,
    type: 'root' | 'irrigation',
  ) => {
    try {
      if (type === 'root') {
        setRootsThreshold(value);
        const { data: response } = await apiClient.rootsConfig.rootsThresholdCreate(
          sectorId, { threshold: value },
        );
        enqueueSnackbar(response.message, { variant: 'success' });
        onConfigSaved?.();
      } else {
        setIrrigationThreshold(value);
        const { data: response } = await apiClient.rootsConfig.irrigationThresholdCreate(
          sectorId, { threshold: value },
        );
        enqueueSnackbar(response.message, { variant: 'success' });
        onConfigSaved?.();
      }
    } catch (err) {
      const { error } = err as FetchError;
      enqueueSnackbar(error.message, { variant: 'error' });
    }
  }, [sectorId, enqueueSnackbar, onConfigSaved]);

  if (isLoading) {
    return <CircularProgress />;
  }

  return (
    <TabContext value={tabValue}>
      <Box sx={{ borderBottom: 0 }}>
        <TabList onChange={handleTabChange} aria-label="sector tabs">
          {!isClientOperator && (
            <Tab label="Configuración" value="1" className={classes.tabLabel} />
          )}
          <Tab label="Alertas" value="2" className={classes.tabLabel} />
          <Tab label="Conversaciones" value="3" className={classes.tabLabel} />
        </TabList>
      </Box>
      {!isClientOperator && (
        <TabPanel value="1" className={classes.configTabPanel}>
          <Card className={classes.configCardRoots}>
            <CardContent>
              <Box className={classes.selectFieldsContainer}>
                <TextField
                  className={classes.selectField}
                  label="Umbral de raíces"
                  type="number"
                  value={rootsThreshold}
                  onChange={(e) => handleThresholdsChange(parseFloat(e.target.value), 'root')}
                  fullWidth
                  variant="standard"
                  inputProps={{
                    step: '0.01',
                    style: { textAlign: 'right' },
                  }}
                />
              </Box>
            </CardContent>
          </Card>
          <Card className={classes.configCardRoots} sx={{ marginTop: 2 }}>
            <CardContent>
              <Box className={classes.selectFieldsContainer}>
                <TextField
                  className={classes.selectField}
                  label="Umbral de irrigación"
                  type="number"
                  value={irrigationThreshold}
                  onChange={
                    (e) => handleThresholdsChange(parseFloat(e.target.value), 'irrigation')
                  }
                  fullWidth
                  variant="standard"
                  inputProps={{
                    step: '0.01',
                    style: { textAlign: 'right' },
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        </TabPanel>
      )}
      <TabPanel value="2" className={classes.configTabPanel}>
        In progress
      </TabPanel>
      <TabPanel value="3" className={classes.configTabPanel}>
        <ConversationsTab
          setModalState={setModalState}
          sectorId={sectorId}
          chartName={ROOTS_NAME}
          refreshCounter={refreshCounter}
        />
      </TabPanel>
    </TabContext>
  );
};

export default RootsConfigContent;
